import { isHttpClientError } from '@deepcloud/api-http-client';

const AUTOGRAPH_FILE_TOO_BIG_MESSAGE_ID = 'error.autograph.file.tooBig';

// The autograph translations live under `errors.api.autograph.file.*`, which
// none of the generic `errors.api.<messageId>` lookups in SignErrorDialog can
// reach - these messageIds have to be mapped explicitly.
const AUTOGRAPH_ERROR_PATHS: Record<string, string> = {
  // A user managed to upload an autograph image the BE later rejects as too big.
  [AUTOGRAPH_FILE_TOO_BIG_MESSAGE_ID]: 'errors.api.autograph.file.too_big',
  // The stored autograph still uses a format the BE no longer supports - it was
  // uploaded before the allowed formats were restricted to PNG/JPEG, and the
  // only way out for the user is to replace it.
  'error.autograph.file.invalid': 'errors.api.autograph.file.invalid'
};

/**
 * Returns the translation key for an autograph error reported by the sign
 * endpoint, or `null` when the messageId is not an autograph error.
 */
export function getAutographErrorPath(messageId: unknown): string | null {
  if (typeof messageId !== 'string') {
    return null;
  }
  return AUTOGRAPH_ERROR_PATHS[messageId] ?? null;
}

// The autograph and the signature preview endpoints reject an oversized
// autograph with the same messageId for two different reasons - only the extra
// payload fields tell them apart: `maxFileSize.mb` for the raw file size,
// `maxLongSide.px` / `maxShortSide.px` (plus the actual `width.px` /
// `height.px`) for the image dimensions.
interface AutographTooBigPayload {
  messageId?: string;
  'maxFileSize.mb'?: number;
}

export interface AutographTooBigMessage {
  key: string;
  params: Record<string, number>;
}

function getResponseData(error: unknown): unknown {
  if (isHttpClientError(error)) {
    return error.response?.data;
  }
  if (typeof error === 'object' && error !== null && 'response' in error) {
    return (error as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
}

// The signature preview is requested as a blob, so its error body arrives as a
// Blob (or as raw text) instead of a parsed object - read it before inspecting.
async function parseErrorPayload(
  data: unknown
): Promise<AutographTooBigPayload | undefined> {
  if (!data) return undefined;

  let raw: string | undefined;
  if (data instanceof Blob) {
    raw = await data.text();
  } else if (typeof data === 'string') {
    raw = data;
  }

  if (raw !== undefined) {
    try {
      return JSON.parse(raw) as AutographTooBigPayload;
    } catch {
      return undefined;
    }
  }

  return typeof data === 'object'
    ? (data as AutographTooBigPayload)
    : undefined;
}

/**
 * Returns the translation key (and its params) describing why the BE rejected
 * an autograph image, or `null` when the error is not an autograph size error.
 */
export async function getAutographTooBigMessage(
  error: unknown
): Promise<AutographTooBigMessage | null> {
  const payload = await parseErrorPayload(getResponseData(error));

  if (payload?.messageId !== AUTOGRAPH_FILE_TOO_BIG_MESSAGE_ID) {
    return null;
  }

  const maxFileSizeMb = payload['maxFileSize.mb'];
  if (typeof maxFileSizeMb === 'number') {
    return {
      key: 'errors.api.autograph.file.too_big_file_size',
      params: { size: maxFileSizeMb }
    };
  }

  return { key: 'errors.api.autograph.file.too_big', params: {} };
}
